import React, { useState, useEffect } from 'react'
import { Plus, Minus } from 'lucide-react'
import PanelModal from '../../../../../ReusableComponents/UI/PanelModal'
import {
    AI_START_ITEMS,
    VAR_TYPES,
    kindOfId,
} from '../../../../../WebAi/aiVariables'
import style from './StartPanel.module.css'

const fullStartButtonState = [
    { name: 'Bicounter1', id: 'Bic1', isEnabled: false },
    { name: 'Bicounter2', id: 'Bic2', isEnabled: false },
    { name: 'BT TX', id: 'BtTx', isEnabled: false },
    { name: 'Biflag1', id: 'Bif1', isEnabled: false },
    { name: 'Biflag2', id: 'Bif2', isEnabled: false },
    { name: 'BT RX', id: 'BtRx', isEnabled: false },
    { name: 'USB TX', id: 'UsbTx', isEnabled: false },
    { name: 'USB RX', id: 'UsbRx', isEnabled: false },
    { name: 'BT Remote', id: 'BtRte', isEnabled: false },
]

// AI mode manages counters/data/flags dynamically (add/remove via the picker);
// hardware app mode keeps the full serial list of fixed toggles. Read at mount
// (not module load) since webAiMode is set when entering AI mode, which can
// happen after this file is imported.
const aiCodeMode = () => sessionStorage.getItem('webAiMode') === 'true'
const buildInitialState = () =>
    aiCodeMode()
        ? AI_START_ITEMS.map((i) => ({ ...i }))
        : fullStartButtonState.map((i) => ({ ...i }))

function StartPanel({ handleClose }) {
    const [startButtonState, setStartButtonState] = useState(buildInitialState)

    useEffect(() => {
        const base = buildInitialState()
        const isPrevStatePresent =
            sessionStorage.getItem('startButtonState') || false
        if (!isPrevStatePresent) {
            // No saved state yet: make sure the list matches the current mode
            // (module-load could have run before webAiMode was set).
            setStartButtonState(base)
            return
        }
        let saved = JSON.parse(isPrevStatePresent)
        // In AI mode ignore any stale hardware-mode items so only the AI
        // variables show, preserving their enabled state.
        if (aiCodeMode()) {
            saved = base.map((item) => {
                const prev = saved.find((s) => s.id === item.id)
                return prev ? { ...item, isEnabled: !!prev.isEnabled } : item
            })
        }
        setStartButtonState(saved)
    }, [])

    useEffect(() => {
        sessionStorage.setItem(
            'startButtonState',
            JSON.stringify(startButtonState)
        )
    }, [startButtonState])

    // Hardware mode: plain toggle of a single item.
    const handleClick = (params) => {
        setStartButtonState((initialState) => {
            return initialState.map((item) => {
                if (item.id === params.target.id) {
                    item.isEnabled = !item.isEnabled
                }
                return item
            })
        })
    }

    // Enable the first n of a kind, disable the rest — so each kind stays a
    // contiguous run (Counter1..CounterN) with no gaps.
    const setKindCount = (kind, n) => {
        const type = VAR_TYPES.find((t) => t.kind === kind)
        if (!type) return
        setStartButtonState((prev) =>
            prev.map((item) => {
                if (kindOfId(item.id) !== kind) return item
                const idx = Number(item.id.replace(type.prefix, '')) || 0
                return { ...item, isEnabled: idx <= n }
            })
        )
    }

    const aiMode = aiCodeMode()
    const kindCount = (kind) =>
        startButtonState.filter(
            (i) => i.isEnabled && kindOfId(i.id) === kind
        ).length

    if (!aiMode) {
        return (
            <PanelModal handleClose={handleClose}>
                <div className={style.buttonGrp}>
                    {startButtonState.map((item) => (
                        <button
                            id={item.id}
                            key={item.id}
                            className={item.isEnabled ? style.enabled : undefined}
                            onClick={handleClick}
                        >
                            {item.name}
                        </button>
                    ))}
                </div>
            </PanelModal>
        )
    }

    // AI mode: one row per variable type. The first "+" turns that type on
    // (count 1); -/+ then add or remove more, up to the per-type limit.
    return (
        <PanelModal handleClose={handleClose}>
            <div className={style.aiVars}>
                {VAR_TYPES.map((t) => {
                    const count = kindCount(t.kind)
                    return (
                        <div
                            key={t.kind}
                            className={`${style.varRow} ${
                                count > 0 ? style.varRowOn : ''
                            }`}
                        >
                            <span className={style.varRowLabel}>{t.label}</span>
                            <div className={style.varStepGroup}>
                                <button
                                    type="button"
                                    className={style.varStep}
                                    disabled={count <= 0}
                                    onClick={() =>
                                        setKindCount(t.kind, count - 1)
                                    }
                                    aria-label={`Remove a ${t.label}`}
                                >
                                    <Minus size={20} strokeWidth={2.4} />
                                </button>
                                <span className={style.varCount}>{count}</span>
                                <button
                                    type="button"
                                    className={style.varStep}
                                    disabled={count >= t.max}
                                    onClick={() =>
                                        setKindCount(t.kind, count + 1)
                                    }
                                    aria-label={`Add a ${t.label}`}
                                >
                                    <Plus size={20} strokeWidth={2.4} />
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>
        </PanelModal>
    )
}

export default StartPanel
